//@flow
import type {DefaultSelectedAdmins, Location} from './type';
import type {ShippingDataAdvanced} from '../../../../../../../entities';
import type {
  Admin1Type,
  Admin2Type,
  Admin3Type,
} from '../../../../../../../utils/location/api';

export const getCheckedState = (
  adminId: string,
  defaultSelectedLevel?: DefaultSelectedAdmins
): boolean => {
  if (!defaultSelectedLevel) {
    return false;
  }

  if (typeof defaultSelectedLevel === 'boolean') {
    return defaultSelectedLevel;
  }

  if (typeof defaultSelectedLevel[adminId] === 'boolean') {
    return defaultSelectedLevel[adminId];
  }

  return false;
};

export const getCheckedStateAdmin3 = (
  admin1Id: string,
  admin3Id: string,
  countryCode: string,
  shippingDataAdvanced: ShippingDataAdvanced,
  defaultSelectedLevel?: DefaultSelectedAdmins,
  location: string
): boolean => {
  if (!location || !shippingDataAdvanced) {
    return false;
  }

  const includeUrlNew = location.includes('new');
  const {storeShippingMethodAdvancedAdminGroups} = shippingDataAdvanced;

  if (!Array.isArray(storeShippingMethodAdvancedAdminGroups)) {
    return false;
  }

  for (const groupLocation of storeShippingMethodAdvancedAdminGroups) {
    const groupLocationByCountry = groupLocation.location?.[countryCode];
    if (!groupLocationByCountry) continue;

    const admin1Location = groupLocationByCountry?.[admin1Id];
    if (includeUrlNew) return false;

    if (typeof admin1Location === 'boolean') return true;
  }

  return false;
};

export const isIndeterminate = (
  adminId: string,
  defaultSelectedLevel?: DefaultSelectedAdmins
): boolean => {
  if (!defaultSelectedLevel) {
    return false;
  }

  if (typeof defaultSelectedLevel === 'boolean') {
    return false;
  }

  if (typeof defaultSelectedLevel[adminId] === 'object') {
    return (
      Object.values(defaultSelectedLevel[adminId]).some(
        value => value === true || value === false
      ) ||
      Object.values(defaultSelectedLevel[adminId]).some(
        value => typeof value === 'object'
      )
    );
  }

  return false;
};

export const isDisabledAdmin1 = (
  admin1Id: string,
  countryCode: string,
  locationCollapse?: Admin1Type,
  shippingDataAdvanced: ShippingDataAdvanced,
  isChecked: {isChecked: boolean}
): boolean => {
  if (!shippingDataAdvanced || !countryCode || !admin1Id) return false;

  const isAdminChecked = isChecked.isChecked;
  const {storeShippingMethodAdvancedAdminGroups} = shippingDataAdvanced;

  for (const groupLocation of storeShippingMethodAdvancedAdminGroups) {
    const groupLocationByCountry = groupLocation?.location?.[countryCode];

    if (!groupLocationByCountry) continue;

    const admin1Location = groupLocationByCountry[admin1Id];

    if (typeof admin1Location === 'object') {
      return false;
    }

    if (typeof admin1Location === 'boolean') {
      return !isAdminChecked;
    }
  }

  return false;
};

export const isDisabledAdmin2 = (
  admin1Id: string,
  admin2Id: string,
  countryCode: string,
  locationCollapse?: Admin2Type,
  shippingDataAdvanced: ShippingDataAdvanced,
  isChecked: {isChecked: boolean, location: string}
): boolean => {
  if (!shippingDataAdvanced || !countryCode || !admin1Id || !admin2Id)
    return false;

  const includeUrlNew = isChecked.location.includes('new');
  const isAdminChecked = isChecked.isChecked;
  const {storeShippingMethodAdvancedAdminGroups} = shippingDataAdvanced;

  for (const groupLocation of storeShippingMethodAdvancedAdminGroups) {
    const groupLocationByCountry = groupLocation?.location?.[countryCode];

    if (!groupLocationByCountry) continue;

    const admin1Location = groupLocationByCountry[admin1Id];

    if (typeof admin1Location === 'object' && admin1Location !== null) {
      const admin2Location = admin1Location[admin2Id];

      if (typeof admin2Location === 'boolean') {
        return !isAdminChecked;
      }

      if (typeof admin2Location === 'object' && admin2Location !== null) {
        return false;
      }
    } else if (typeof admin1Location === 'boolean') {
      return includeUrlNew;
    }
  }

  return false;
};

export const isDisabledAdmin3 = (
  admin1Id: string,
  admin2Id: string,
  admin3Id: string,
  countryCode: string,
  locationCollapse?: Admin3Type,
  shippingDataAdvanced: ShippingDataAdvanced,
  isChecked: {isChecked: boolean, location: string}
): boolean => {
  if (
    !shippingDataAdvanced ||
    !countryCode ||
    !admin1Id ||
    !admin2Id ||
    !admin3Id
  ) {
    return false;
  }

  const includeUrlNew = isChecked.location.includes('new');
  const isAdminChecked = isChecked.isChecked;
  const {storeShippingMethodAdvancedAdminGroups} = shippingDataAdvanced;

  for (const groupLocation of storeShippingMethodAdvancedAdminGroups) {
    const groupLocationByCountry = groupLocation?.location?.[countryCode];

    if (!groupLocationByCountry) {
      continue;
    }

    const admin1Location = groupLocationByCountry[admin1Id];

    if (admin1Location && typeof admin1Location === 'object') {
      const admin2Location = admin1Location[admin2Id];

      if (admin2Location && typeof admin2Location === 'object') {
        const admin3Location = admin2Location[admin3Id];

        if (typeof admin3Location === 'boolean') {
          return !isAdminChecked;
        }

        if (typeof admin3Location === 'object' && admin3Location !== null) {
          return false;
        }
      }

      if (typeof admin2Location === 'boolean') {
        return includeUrlNew;
      }
    } else if (typeof admin1Location === 'boolean') {
      return includeUrlNew;
    }
  }

  return false;
};

type NestedObject = {
  [key: string]: string | boolean | NestedObject,
};

export const removeEmptyObjects = (
  obj: NestedObject
): NestedObject | Object => {
  if (typeof obj !== 'object' || obj === null) return obj;

  const newObj: NestedObject = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];

      if (typeof value === 'object' && value !== null) {
        const cleanedValue = removeEmptyObjects(value);

        if (cleanedValue && Object.keys(cleanedValue).length > 0) {
          newObj[key] = cleanedValue;
        }
      } else if (typeof value === 'boolean') {
        newObj[key] = value;
      }
    }
  }

  return Object.keys(newObj).length > 0 ? newObj : {};
};

export const hasTrueValues = (obj: NestedObject) => {
  for (let key in obj) {
    if (typeof obj[key] === 'object') {
      if (hasTrueValues(obj[key])) return true;
    } else if (obj[key] === true) {
      return true;
    }
  }
  return false;
};

export function processLocationsNodes(
  locations: Location[],
  level: NestedObject,
  defaultLvl: NestedObject
) {
  const newLevel = {...level};

  locations.forEach(location => {
    const locationId = String(location.adminId);

    if (!newLevel[locationId] && typeof defaultLvl[locationId] === 'object') {
      newLevel[locationId] = {...defaultLvl[locationId]};
    }

    if (location.isChecked && !location.indeterminate) {
      newLevel[locationId] = true;
    } else if (location.indeterminate || location.isExpanded) {
      if (!newLevel[locationId] || typeof newLevel[locationId] !== 'object') {
        newLevel[locationId] = {};
      }

      if (location.subLocations.length > 0) {
        newLevel[locationId] = processLocationsNodes(
          location.subLocations,
          // $FlowFixMe
          newLevel[locationId],
          // $FlowFixMe
          defaultLvl[locationId] || {}
        );

        const allSubnodesChecked = location.subLocations.every(
          // $FlowFixMe
          subLoc => newLevel[locationId][subLoc.adminId] === true
        );

        if (allSubnodesChecked && !location.indeterminate) {
          newLevel[locationId] = true;
        }
      }
    } else {
      delete newLevel[locationId];
    }

    if (
      typeof newLevel[locationId] === 'object' &&
      Object.keys(newLevel[locationId]).length === 0
    ) {
      delete newLevel[locationId];
    }
  });

  return newLevel;
}
